import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilterBar,
  parseQuery,
  QueryBuilder,
  useQueryBuilderContext,
} from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { ParsedToken, PinnedOption } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions } from '@gonfalon/dataviz-experimental/types';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { FieldGroup, Label } from '@launchpad-ui/components';
import clsx from 'clsx';

import {
  type ContextKindConflict,
  getContextKindConflict,
} from '../../../CreateMetricModal/MetricEventQueryBuilder/eventQueryConversion';
import { EventFilterInfoTooltip } from '../components/EventFilterInfoTooltip';

import { CONTEXT_KIND_FIELD } from './metricEventFilterFields';
import { isContextField, mergeSeedFieldOptions, seedFieldOptions } from './seedAndScopeFieldOptions';
import { useContextBarFieldOptions } from './useContextBarFieldOptions';
import { useContextKindOptions, useEventFieldOptions } from './useMetricEventSuggestions';
import { usePinnedContextKind } from './usePinnedContextKind';

import styles from './MetricEventQueryBuilder.module.css';

const contextKindConflictMessage = (conflict: ContextKindConflict) =>
  conflict === 'pinned' ? msg('metrics.eventFilters.pinnedConflict') : msg('metrics.eventFilters.multipleContextKinds');

const ContextFilterBar: React.FC<{
  isEditable: boolean;
  inputId?: string;
  fieldOptions: FieldOptions[];
  ariaLabelledby?: string;
  isInvalid?: boolean;
  pinnedOptions: PinnedOption[];
}> = ({ isEditable, inputId, fieldOptions, ariaLabelledby, isInvalid, pinnedOptions }) => {
  const projectKey = useProjectKey();
  const { parsedQuery, dispatch } = useQueryBuilderContext();
  const { selectedKinds, pinnedValue, effectivePinnedOptions, onPinnedChange } = usePinnedContextKind({
    parsedQuery,
    dispatch,
    pinnedOptions,
    isEditable,
  });
  const { scopedFieldOptions, onLoadValues, showEmptyPinnedAttributesMessage } = useContextBarFieldOptions(
    projectKey,
    fieldOptions,
    selectedKinds,
    parsedQuery,
  );

  return (
    <div className={styles.queryBuilderFilters}>
      <FilterBar
        readonly={!isEditable}
        fieldOptions={scopedFieldOptions}
        omitFields={[CONTEXT_KIND_FIELD]}
        allowExistsOption={false}
        inputId={inputId}
        ariaLabelledby={ariaLabelledby}
        isInvalid={isInvalid}
        showSearchIcon={isEditable}
        placeholder=""
        pinnedOptions={effectivePinnedOptions}
        pinnedValue={pinnedValue}
        onPinnedChange={onPinnedChange}
        pinnedPlaceholder={msg('metrics.eventFilters.allContextKinds')}
        pinnedAriaLabel={msg('metrics.eventFilters.pinnedContextKindLabel')}
        onLoadValues={onLoadValues}
        autocompleteHeader={
          showEmptyPinnedAttributesMessage ? msg('metrics.eventFilters.noPinnedAttributes') : undefined
        }
      />
    </div>
  );
};

const EventFilterBar: React.FC<{
  isEditable: boolean;
  inputId?: string;
  fieldOptions: FieldOptions[];
  ariaLabelledby?: string;
}> = ({ isEditable, inputId, fieldOptions, ariaLabelledby }) => (
  <div className={styles.queryBuilderFilters}>
    <FilterBar
      readonly={!isEditable}
      fieldOptions={fieldOptions}
      allowExistsOption={false}
      inputId={inputId}
      ariaLabelledby={ariaLabelledby}
      showSearchIcon={isEditable}
      placeholder=""
    />
  </div>
);

interface MetricEventQueryBuilderProps {
  contextDefaultValue?: string;
  eventDefaultValue?: string;
  isEditable?: boolean;
  inputId?: string;
  onChange?: (bars: {
    contextTokens: ParsedToken[];
    eventTokens: ParsedToken[];
    editedBar: 'context' | 'event';
  }) => void;
  errorMessage?: string;
  className?: string;
}

export const MetricEventQueryBuilder: React.FC<MetricEventQueryBuilderProps> = ({
  contextDefaultValue,
  eventDefaultValue,
  isEditable = true,
  inputId,
  onChange,
  errorMessage,
  className,
}) => {
  const projectKey = useProjectKey();
  const fieldOptions = useEventFieldOptions(projectKey);
  const contextKindOptions = useContextKindOptions(projectKey);
  const contextSeedOptions = useMemo(() => seedFieldOptions(contextDefaultValue), [contextDefaultValue]);
  const eventSeedOptions = useMemo(() => seedFieldOptions(eventDefaultValue), [eventDefaultValue]);
  const contextFieldOptions = useMemo(
    () =>
      mergeSeedFieldOptions(
        fieldOptions.filter((o) => isContextField(o.field)),
        contextSeedOptions,
      ),
    [fieldOptions, contextSeedOptions],
  );
  const eventFieldOptions = useMemo(
    () =>
      mergeSeedFieldOptions(
        fieldOptions.filter((o) => !isContextField(o.field)),
        eventSeedOptions,
      ),
    [fieldOptions, eventSeedOptions],
  );

  const contextInputId = inputId ? `${inputId}-context` : 'context';
  const eventInputId = inputId ? `${inputId}-event` : 'event';
  const contextLabelId = `${contextInputId}-label`;
  const eventLabelId = `${eventInputId}-label`;

  const [contextKindConflict, setContextKindConflict] = useState<ContextKindConflict | null>(null);

  // Seed both bars' refs from defaults so editing one doesn't drop the other's stored filters.
  const contextTokensRef = useRef<ParsedToken[]>(parseQuery(contextDefaultValue ?? ''));
  const eventTokensRef = useRef<ParsedToken[]>(parseQuery(eventDefaultValue ?? ''));
  const contextEditedRef = useRef(false);
  const eventEditedRef = useRef(false);

  useEffect(() => {
    if (!contextEditedRef.current) {
      const tokens = parseQuery(contextDefaultValue ?? '');
      contextTokensRef.current = tokens;
      setContextKindConflict(getContextKindConflict(tokens));
    }
  }, [contextDefaultValue]);
  useEffect(() => {
    if (!eventEditedRef.current) {
      eventTokensRef.current = parseQuery(eventDefaultValue ?? '');
    }
  }, [eventDefaultValue]);

  const handleChange = useCallback(
    (which: 'context' | 'event', tokens: ParsedToken[]) => {
      if (which === 'context') {
        contextTokensRef.current = tokens;
        contextEditedRef.current = true;
        setContextKindConflict(getContextKindConflict(tokens));
      } else {
        eventTokensRef.current = tokens;
        eventEditedRef.current = true;
      }
      onChange?.({
        contextTokens: contextTokensRef.current,
        eventTokens: eventTokensRef.current,
        editedBar: which,
      });
    },
    [onChange],
  );

  return (
    <div className={clsx(styles.queryBuilders, className)}>
      <QueryBuilder
        key={contextDefaultValue}
        defaultValue={contextDefaultValue}
        onChange={(event) => handleChange('context', event.tokens)}
      >
        <FieldGroup
          className={styles.filterGroup}
          errorMessage={contextKindConflict ? contextKindConflictMessage(contextKindConflict) : undefined}
        >
          {isEditable && (
            <div className={styles.filterHeader}>
              <Label id={contextLabelId}>
                <Message id="metrics.eventFilters.filterByContext" />
              </Label>
              <EventFilterInfoTooltip labelText={msg('metrics.eventFilters.aboutContextFilters')}>
                <Message
                  id="metrics.eventFilters.contextAttributeHelp"
                  values={{ code: (chunks) => <code>{chunks}</code> }}
                />
              </EventFilterInfoTooltip>
            </div>
          )}
          <ContextFilterBar
            isEditable={isEditable}
            inputId={contextInputId}
            ariaLabelledby={isEditable ? contextLabelId : undefined}
            fieldOptions={contextFieldOptions}
            isInvalid={contextKindConflict !== null}
            pinnedOptions={contextKindOptions}
          />
        </FieldGroup>
      </QueryBuilder>
      <QueryBuilder
        key={eventDefaultValue}
        defaultValue={eventDefaultValue}
        onChange={(event) => handleChange('event', event.tokens)}
      >
        <FieldGroup className={styles.filterGroup} errorMessage={errorMessage}>
          {isEditable && (
            <div className={styles.filterHeader}>
              <Label id={eventLabelId}>
                <Message id="metrics.eventFilters.filterByEvent" />
              </Label>
              <EventFilterInfoTooltip labelText={msg('metrics.eventFilters.aboutEventPropertyFilters')}>
                <Message
                  id="metrics.eventFilters.eventPropertyHelp"
                  values={{ code: (chunks) => <code>{chunks}</code> }}
                />
              </EventFilterInfoTooltip>
            </div>
          )}
          <EventFilterBar
            isEditable={isEditable}
            inputId={eventInputId}
            ariaLabelledby={isEditable ? eventLabelId : undefined}
            fieldOptions={eventFieldOptions}
          />
        </FieldGroup>
      </QueryBuilder>
    </div>
  );
};
